import { Typography } from "@atoms/typography";
import { BookingContactForm } from "@molecules/booking-contact-form";
import { ContactSection } from "@organisms/contact-section";
import { ProductsGrid } from "@organisms/products-grid";
import { Searcher, SearchForm } from "@organisms/searcher";
import { LayoutTemplate } from "@templates/layout";
import React, { FC, useCallback, useMemo } from "react";
import { Trans, useTranslation } from "next-i18next";
import styled from "styled-components";
import { routes } from "@fhlib/routes";
import router, { useRouter } from "next/router";
import * as ga from "@fhlib/ga";
import queryString from "query-string";
import { BookingContactFormData } from "@molecules/booking-contact-form/types";
import { PropertyCardProps } from "@molecules/property-card/property-card";
import { BookingNoResults } from "@molecules/booking-no-results";

export type ReservationsTemplateProps = {
  onContact: (form: BookingContactFormData) => Promise<boolean>;
  properties: PropertyCardProps[];
  searchInitialForm?: SearchForm;
};

export const ReservationsTemplate: FC<ReservationsTemplateProps> = ({
  onContact,
  properties,
  searchInitialForm,
}) => {
  const router = useRouter();
  const { t } = useTranslation("homes");

  const handleSearch = useCallback(
    (form: SearchForm) => {
      ga.event("availability-filter", form);
      let raw = queryString.stringify(
        {
          check_in:
            typeof form.checkIn === "string"
              ? form.checkIn
              : form.checkIn?.toISOString().substring(0, 10),
          check_out:
            typeof form.checkOut === "string"
              ? form.checkOut
              : form.checkOut?.toISOString().substring(0, 10),
          guests: form.guests,
        },
        { skipEmptyString: true }
      );
      if (raw) raw = "?" + raw;
      router.push(routes["homes-results"][router?.locale ?? "en"] + raw);
    },
    [router]
  );

  const initialContactData = useMemo<Partial<BookingContactFormData>>(() => {
    return {
      guests: searchInitialForm?.guests,
      checkin: searchInitialForm?.checkIn,
      checkout: searchInitialForm?.checkOut,
    };
  }, [searchInitialForm]);

  return (
    <LayoutTemplate page="reservations">
      <Searcher
        sticky
        onSearch={handleSearch}
        initialForm={searchInitialForm}
      />
      <Container>
        <Typography variant="title1">{t("title")}</Typography>
        {properties.length > 0 ? (
          <ProductsGrid
            products={properties}
            searchFormData={searchInitialForm}
          />
        ) : (
          <BookingNoResults />
        )}
      </Container>
      <ContactSection
        highlightBackground
        form={BookingContactForm}
        onSubmit={onContact}
        initialData={initialContactData}
      />
    </LayoutTemplate>
  );
};

const Container = styled.div`
  padding: 0 1em 3em 1em;
`;
